import { Platform } from 'react-native';
import Constants from 'expo-constants';

// Proteção contra falhas (0.57): guarda os erros no aparelho (para mandar à equipe), evita que um erro depois que o app
// já abriu feche tudo e percebe quando o app fechou sozinho seguidas vezes ao abrir (aí oferece o modo seguro).
// A gravação é síncrona: no erro fatal não dá tempo de esperar o armazenamento normal.

export type Falha = { quando: string; onde: string; msg: string; pilha?: string; fatal?: boolean; versao: string; aparelho: string };
type Estado = { pendente: boolean; seguidas: number };
const MAX = 20;
const WEB = Platform.OS === 'web';

function arquivo(nome: string): any {
  if (WEB) return null;
  try { const { File, Paths } = require('expo-file-system'); return new File(Paths.document, nome); } catch { return null; }
}
function lerTexto(nome: string): string | null {
  try {
    if (WEB) return typeof localStorage !== 'undefined' ? localStorage.getItem('mp_' + nome) : null;
    const f = arquivo(nome);
    return f?.exists ? f.textSync() : null;
  } catch { return null; }
}
function gravarTexto(nome: string, txt: string) {
  try {
    if (WEB) { if (typeof localStorage !== 'undefined') localStorage.setItem('mp_' + nome, txt); return; }
    const f = arquivo(nome);
    if (!f) return;
    if (!f.exists) f.create({ overwrite: true });
    f.write(txt);
  } catch { /* sem onde gravar: segue */ }
}
const lerJson = <T,>(nome: string, padrao: T): T => { try { const s = lerTexto(nome); return s ? JSON.parse(s) as T : padrao; } catch { return padrao; } };

/** Versão do app e do pacote, e o aparelho (marca, modelo e sistema), para a equipe saber onde aconteceu. */
export function versaoEAparelho() {
  let versao = Constants.expoConfig?.version ?? '?';
  try { const v = (globalThis as any).expo?.modules?.ExpoApplication?.nativeApplicationVersion; if (v && v !== versao) versao = `app ${v} · pacote ${versao}`; } catch { /* sem módulo */ }
  const c: any = (Platform as any).constants ?? {};
  const aparelho = WEB ? `web · ${typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : ''}`
    : Platform.OS === 'android' ? `Android ${c.Release ?? Platform.Version} · ${[c.Manufacturer, c.Brand !== c.Manufacturer ? c.Brand : '', c.Model].filter(Boolean).join(' ')}`
    : `iOS ${Platform.Version} · ${c.interfaceIdiom ?? ''}`;
  return { versao, aparelho };
}

export const falhas = (): Falha[] => lerJson<Falha[]>('falhas.json', []);
export function limparFalhas() { gravarTexto('falhas.json', '[]'); avisar(); }

/** Guarda um erro (os 20 mais recentes). */
export function registrarFalha(e: unknown, onde: string, fatal = false) {
  try {
    const err = e as any;
    const msg = String(err?.message ?? err ?? '?').slice(0, 600);
    const pilha = String(err?.stack ?? '').split('\n').slice(0, 14).join('\n').slice(0, 2000) || undefined;
    const { versao, aparelho } = versaoEAparelho();
    const nova: Falha = { quando: new Date().toISOString(), onde: onde.slice(0, 300), msg, pilha, fatal, versao, aparelho };
    const l = falhas();
    // o mesmo erro repetido em seguida não ocupa a lista toda
    if (l[0] && l[0].msg === nova.msg && l[0].onde === nova.onde) l[0] = { ...nova };
    else l.unshift(nova);
    gravarTexto('falhas.json', JSON.stringify(l.slice(0, MAX)));
    avisar();
  } catch { /* nunca deixa o registro causar outro erro */ }
}

/** Texto do relatório para mandar à equipe. */
export function textoRelatorio(lista: Falha[] = falhas()) {
  const { versao, aparelho } = versaoEAparelho();
  const linhas = [`My Planner Anne ${versao}`, aparelho, ''];
  for (const f of lista.slice(0, 8)) {
    linhas.push(`[${f.quando.slice(0, 16).replace('T', ' ')}] ${f.fatal ? 'FATAL · ' : ''}${f.onde}`, f.msg);
    if (f.pilha) linhas.push(f.pilha.split('\n').slice(0, 6).join('\n'));
    linhas.push('');
  }
  return linhas.join('\n').slice(0, 4900);
}

// ---------- quem quer saber de erros novos (a tela de aviso, os Ajustes) ----------
const ouvintes = new Set<() => void>();
let versaoLista = 0;
function avisar() { versaoLista++; ouvintes.forEach(f => { try { f(); } catch { /* segue */ } }); }
export const ouvirFalhas = (f: () => void) => { ouvintes.add(f); return () => { ouvintes.delete(f); }; };
export const versaoFalhas = () => versaoLista;

// erro que fecharia o app depois que ele já abriu: mostra o aviso em vez de fechar
let ultimoGrave: Falha | null = null;
export const falhaGrave = () => ultimoGrave;
export function dispensarFalhaGrave() { ultimoGrave = null; avisar(); }

// ---------- arranque e modo seguro ----------
let seguidas = 0;
let seguro = false;
export const fechouSeguidas = () => seguidas;
export const modoSeguro = () => seguro;
export function ligarModoSeguro(sim: boolean) { seguro = sim; avisar(); }

let instalado = false;
let estavel = false;
/** Chamada uma vez ao abrir o app. */
export function iniciarProtecao() {
  if (instalado) return;
  instalado = true;
  // se a abertura anterior não chegou a ficar estável (15 s), o app fechou sozinho
  const est = lerJson<Estado>('arranque.json', { pendente: false, seguidas: 0 });
  seguidas = est.pendente ? Math.min(9, (est.seguidas ?? 0) + 1) : 0;
  gravarTexto('arranque.json', JSON.stringify({ pendente: true, seguidas }));
  setTimeout(() => { estavel = true; gravarTexto('arranque.json', JSON.stringify({ pendente: false, seguidas: 0 })); }, 15000);

  const EU: any = (globalThis as any).ErrorUtils;
  if (!EU?.setGlobalHandler) {
    if (WEB && typeof window !== 'undefined') {
      window.addEventListener('error', ev => registrarFalha(ev.error ?? ev.message, 'janela'));
      window.addEventListener('unhandledrejection', ev => registrarFalha((ev as any).reason, 'promessa'));
    }
    return;
  }
  const anterior = EU.getGlobalHandler?.();
  EU.setGlobalHandler((e: any, fatal?: boolean) => {
    registrarFalha(e, fatal ? 'erro fatal' : 'erro', !!fatal);
    // logo ao abrir, deixa o sistema tratar (assim uma atualização com defeito volta sozinha para a anterior)
    if (fatal && estavel) { ultimoGrave = falhas()[0] ?? null; avisar(); return; }
    anterior?.(e, fatal);
  });
}
